import React, {useMemo} from 'react';
import SidebarLayout from '../../dashboard/components/SidebarLayout';
import {StoreMapRefactored} from '../../../Presentation/components/common/StoreMapRefactored';
import CityMapControls from '../../../Presentation/components/common/CityMapControls';
import StoreDetails from '../../../Presentation/components/common/StoreDetails';
import MarketerDetails from '../../../Presentation/components/common/MarketerDetails';
import {useDashboardManagement} from '../../dashboard/hooks/useDashboardManagement';
import {Store, Users, AlertCircle} from 'lucide-react';
import {PermissionDeniedCard} from '../../../Presentation/components/common';
import {useAuthorization} from '../../auth/hooks/useAuthorization';
import {PERMISSIONS} from '../../../constants/permissions';
import {MAP_LAYOUT} from '../constants/mapLayout.constants';

const MapPage: React.FC = () => {
  const {hasPermission} = useAuthorization();

  // Unified dashboard management
  const {
    // UI State
    viewMode, setViewMode,
    showStores, showMarketers,

    // Data
    user, isProvider, brandNames,
    selectedStore, selectedMarketer,
    selectedStoreCount, selectedMarketerCount,
    currentStoresError, marketersError,

    // Actions
    handleViewModeChange,
    handleToggleStores, handleToggleMarketers
  } = useDashboardManagement();

  // Computed values
  const hasMapAccess = useMemo(() => {
    return hasPermission(PERMISSIONS.MANAGE_STORES) || hasPermission(PERMISSIONS.MANAGE_MARKETERS);
  }, [hasPermission]);

  const shouldShowDetailsPanel = useMemo(() => {
    return (selectedStore && showStores) || (selectedMarketer && showMarketers);
  }, [selectedStore, selectedMarketer, showStores, showMarketers]);

  const detailsPanelClasses = useMemo(() => {
    return `${MAP_LAYOUT.DETAILS_PANEL_WIDTH.MOBILE} ${MAP_LAYOUT.DETAILS_PANEL_WIDTH.DESKTOP} ${MAP_LAYOUT.DETAILS_PANEL_MIN_HEIGHT.MOBILE} ${MAP_LAYOUT.DETAILS_PANEL_MIN_HEIGHT.DESKTOP} ${MAP_LAYOUT.TRANSITION_CLASSES}`;
  }, []);

  return (
    <SidebarLayout>
      <div className="h-full flex flex-col gap-6">
        {/* Provider Info Banner */}
        {isProvider && (
          <div className="bg-brand-50 border border-brand-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-brand-100 rounded-full flex items-center justify-center">
                <span className="text-brand-600 font-semibold text-sm">🗺️</span>
              </div>
              <div>
                <h3 className="text-sm font-medium text-brand-900">
                  خريطة المتاجر - {user?.username || ''}
                </h3>
                <p className="text-xs text-brand-700">
                  البراندات المرتبطة: {brandNames.length > 0 ? brandNames.join(', ') : 'لا توجد براندات مرتبطة'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* View Mode Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-1">
          <div className="flex space-x-1">
            {/* Stores Tab - Show if user has stores permission */}
            {hasPermission(PERMISSIONS.MANAGE_STORES) && (
              <button
                onClick={handleToggleStores}
                className={`
                  flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all duration-200
                  ${showStores
        ? 'bg-brand-600 text-white shadow-sm'
        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
      }
                `}
              >
                <Store className="w-4 h-4" />
                 {isProvider
                   ? `متاجر البراندات (${brandNames.join(', ')})`
                  : 'المتاجر'
                } ({selectedStoreCount > 0 ? `${selectedStoreCount} محدد` : 'غير محدد'})
              </button>
            )}

            {/* Marketers Tab - Show if user has marketers permission */}
            {hasPermission(PERMISSIONS.MANAGE_MARKETERS) && (
              <button
                onClick={handleToggleMarketers}
                className={`
                  flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all duration-200
                  ${showMarketers
        ? 'bg-green-600 text-white shadow-sm'
        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
      }
                `}
              >
                <Users className="w-4 h-4" />
                المسوقين ({selectedMarketerCount > 0 ? `${selectedMarketerCount} محدد` : 'غير محدد'})
              </button>
            )}
          </div>
        </div>

        {/* Error Messages */}
        {showStores && currentStoresError && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-red-700">
              خطأ في تحميل المتاجر: {String(currentStoresError)}
            </span>
          </div>
        )}

        {showMarketers && marketersError && (
          <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-yellow-500" />
            <span className="text-yellow-700">تحذير: {String(marketersError)}</span>
          </div>
        )}

        {/* No Access Message */}
        {!hasPermission(PERMISSIONS.MANAGE_STORES) && !hasPermission(PERMISSIONS.MANAGE_MARKETERS) && (
          <PermissionDeniedCard
            title="لا توجد صلاحيات للخريطة"
            message="ليس لديك صلاحيات للوصول إلى خريطة المتاجر أو المسوقين. يرجى التواصل مع المدير للحصول على الصلاحيات المناسبة."
          />
        )}

        {/* Main Content - Map Interface */}
        {hasMapAccess && (
          <div className="flex-1 flex flex-col lg:flex-row gap-6">
            {/* Left Column - Map and Controls */}
            <div className={`flex-1 flex flex-col space-y-4 ${MAP_LAYOUT.TRANSITION_CLASSES}`}>
              {/* City Map Controls */}
              <CityMapControls />

              {/* Map */}
              <div className="flex-1">
                <StoreMapRefactored
                  viewMode={viewMode}
                  showStores={showStores}
                  showMarketers={showMarketers}
                />
              </div>
            </div>

            {/* Right Column - Details (Only shown when item is selected) */}
            {shouldShowDetailsPanel && (
              <div className={detailsPanelClasses}>
                {selectedStore && showStores ? (
                  <StoreDetails store={selectedStore} />
                ) : selectedMarketer && showMarketers ? (
                  <MarketerDetails marketer={selectedMarketer} />
                ) : null}
              </div>
            )}
          </div>
        )}
      </div>
    </SidebarLayout>
  );
};

export default MapPage;
